import { tv, VariantProps } from "@utils/tw"
import { ComponentProps, forwardRef, ReactNode, useId } from "react"

export type TextFieldProps = Omit<ComponentProps<"input">, "placeholder"> &
  VariantProps<typeof textFieldStyles> & {
    label: string
    error?: ReactNode
  }

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  ({ className, label, error, id, ...props }, ref) => {
    const fallbackId = useId()
    const inputId = id ?? fallbackId
    const errorId = `${inputId}-error`
    const styles = textFieldStyles({ invalid: !!error })

    return (
      <div className={styles.root({ className })}>
        <div className={styles.field()}>
          <input
            {...props}
            ref={ref}
            id={inputId}
            // A blank placeholder lets CSS float the label once the field is focused or filled.
            placeholder=" "
            aria-invalid={error ? true : props["aria-invalid"]}
            aria-describedby={
              [props["aria-describedby"], error ? errorId : null].filter(Boolean).join(" ") || undefined
            }
            className={styles.input()}
          />
          <label htmlFor={inputId} className={styles.label()}>
            {label}
          </label>
        </div>
        {error ? (
          <p id={errorId} className={styles.error()}>
            {error}
          </p>
        ) : null}
      </div>
    )
  },
)
TextField.displayName = "TextField"

export const textFieldStyles = tv({
  slots: {
    root: "w-full text-blue",
    field: "relative",
    input: [
      "peer type-label-m w-full h-buttonLg px-4 bg-white border border-blue rounded-none appearance-none outline-none transition-colors duration-300",
      "[&:placeholder-shown:not(:focus)]:bg-white-tint [&:placeholder-shown:not(:focus)]:border-transparent",
      "autofill:shadow-[inset_0_0_0_100px_#fff] autofill:[-webkit-text-fill-color:currentColor]",
      "disabled:opacity-50",
    ],
    label: [
      "absolute left-4 top-0 type-label-m whitespace-nowrap pointer-events-none origin-left -translate-y-1/2 scale-50 transition-all duration-300",
      // The label's backing notches the border while it is floating.
      "before:absolute before:-inset-x-2 before:inset-y-0 before:-z-10 before:bg-white before:transition-opacity before:duration-300",
      "peer-[:placeholder-shown:not(:focus)]:top-1/2 peer-[:placeholder-shown:not(:focus)]:scale-100 peer-[:placeholder-shown:not(:focus)]:before:opacity-0",
    ],
    error: "type-body-xs text-error-dark mt-2",
  },
  variants: {
    invalid: {
      true: {
        input: "border-error-dark [&:placeholder-shown:not(:focus)]:border-error-dark",
      },
    },
  },
})
